import type { AxiosInstance, CancelToken, ResponseType } from 'axios';

import publicAxios, { apiBuilder } from 'utils/api/common-requests/common-requests';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/** A request to the backend: what the generated request functions (utils/api/generated) describe */
export interface ApiRequest {
    method: HttpMethod;
    url: string;
    /** the query parameters (repeated for arrays: ?id=1&id=2) */
    params?: object;
    /** the body: JSON, or FormData for files */
    data?: unknown;
    /** 'blob' for files */
    responseType?: ResponseType;
}

export interface ApiRequestOptions {
    /** cancel with axios.CancelToken.source() (the project does not use AbortController signals) */
    cancelToken?: CancelToken;
    /** milliseconds; a slower answer rejects the request (no timeout by default) */
    timeout?: number;
}

// apiBuilder is a reselect selector; its auth key is read from the storage, so it is called without the state
const authorizedAxios = (apiBuilder as unknown) as () => AxiosInstance;

/**
 * Sends a request with the authorized axios instance (apiBuilder) and resolves with the response body.
 * An error response rejects with the axios error; its response.data is an ApiErrorDto (utils/api/dto/base).
 */
export const apiRequest = <TResponse>(request: ApiRequest, options: ApiRequestOptions = {}): Promise<TResponse> =>
    authorizedAxios()
        .request<TResponse>({
            ...request,
            cancelToken: options.cancelToken,
            ...(options.timeout ? { timeout: options.timeout } : {})
        })
        .then(response => response.data);

/**
 * Like apiRequest, but without the auth token: the public endpoints (/public/...) of the pages for signed-out users
 * (password reset, account unlock).
 */
export const publicApiRequest = <TResponse>(request: ApiRequest, options: ApiRequestOptions = {}): Promise<TResponse> =>
    publicAxios
        .request<TResponse>({
            ...request,
            cancelToken: options.cancelToken,
            ...(options.timeout ? { timeout: options.timeout } : {})
        })
        .then(response => response.data);
